/**
 * Price Engine Rules:
 * 1. Each player's price can change by a maximum of +0.3 or -0.2 per gameweek. Price changes are applied in increments of 0.1, and partial changes are accumulated.
 * 2. Performance-based price guardrails:
 *    - Did Not Play (DNP): -0.1
 *    - Score < 2: -0.2
 *    - Score = 2: -0.1
 *    - Score = 3: Negative capped at -0.1, but can be 0 if median is low.
 *    - Score >= 4: Cannot decline (min 0 change).
 *    - Scores between 3 and 4: (No scores fall here, so no special handling)
 * 3. Performance changes are calculated as a function of the difference from the median performance, with a scaling factor (e.g., diff * 0.05).
 * 4. Selection change: The difference in selection percentage (normalized to a 12-manager league) from previous gameweek, scaled (e.g., * 0.1), capped at +/-0.3.
 * 5. Combine changes: 80% weight to performance, 20% to selection change.
 * 6. Clamp the final delta between -0.2 and +0.3.
 * 7. Prices are globally capped between 3.5 and 11.0.
 * 8. Price accumulator is reset if a player hits the global cap.
 *
 * Outcome: These rules ensure that price changes are gradual, reward outperformance, penalize poor or no performance, and reflect shifts in selection/popularity, while preventing extreme volatility.
 */
// src/utils/priceEngine.js

import { db } from "../lib/firebase";
import { doc, updateDoc, getDocs } from "firebase/firestore";
import { logError } from "./logError";
import { paths } from "../lib/paths";

/**
 * Fetches performance and selection data for players in a given league and gameweek.
 * @param {Object} firestore - Firestore instance.
 * @param {string} leagueID - League identifier.
 * @param {number} gameweek - Gameweek number.
 * @returns {Promise<{perfData: Object, selectionData: Object}>} - Performance and selection data keyed by player ID.
 */
export async function fetchPerfAndSelectionData(firestore, leagueID, gameweek) {
  try {
    const gwKey =
      typeof gameweek === "number"
        ? `GW${gameweek}`
        : gameweek.startsWith("GW")
        ? gameweek
        : `GW${gameweek}`;
    console.log("🎯 Using gwKey in fetchPerfAndSelectionData:", gwKey);

    // Helper: resolve a player ID from many possible shapes (string, DocumentReference, nested maps)
    function resolvePlayerID(val) {
      if (!val) return null;
      // Direct string (may or may not have a position prefix)
      if (typeof val === "string") {
        // If a UUID-like ID is embedded (end of string), prefer that
        const m = val.match(/[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i);
        if (m) return m[0];
        return val; // already a clean ID
      }
      // Arrays: return first resolvable string
      if (Array.isArray(val)) {
        for (const v of val) {
          const r = resolvePlayerID(v);
          if (typeof r === "string") return r;
        }
        return null;
      }
      // Objects (including Firestore DocumentReference or nested maps)
      if (typeof val === "object") {
        // Firestore v9 DocumentReference has a string `id`
        if (typeof val.id === "string") return val.id;
        if (typeof val.playerID === "string") return val.playerID;
        if (typeof val.playerId === "string") return val.playerId;
        if (typeof val.uid === "string") return val.uid;
        // Fallback: search nested values for a string id
        for (const v of Object.values(val)) {
          const r = resolvePlayerID(v);
          if (typeof r === "string") return r;
        }
      }
      return null;
    }

    const perfData = {};
    const selectionCounts = {};
    let totalTeams = 0;

    // Fetch playerPoints for the gameweek
    const playerPointsCol = paths.gwPlayerPoints(leagueID, gwKey);
    const playerPointsSnapshot = await getDocs(playerPointsCol);
    playerPointsSnapshot.forEach(docSnap => {
      const d = docSnap.data();
      perfData[docSnap.id] = d.total !== undefined ? d.total : (d.totalPoints || 0);
    });

    // Fetch all teams for the gameweek
    const teamsCol = paths.gwTeams(leagueID, gwKey);
    const teamsSnapshot = await getDocs(teamsCol);
    totalTeams = teamsSnapshot.size;

    // Count player selections across all teams
    teamsSnapshot.forEach(teamDoc => {
      const teamData = teamDoc.data();
      if (!teamData || !teamData.selections) return;

      // Support both shapes: selections[gwKey] (nested by GW) OR selections directly keyed by positions
      const selections = teamData.selections;

      // Iterate over each position (DEF, MID, FWD)
      const gwSelections = selections[gwKey] ? selections[gwKey] : selections;
      console.log("📂 Using gwSelections object:", gwSelections);

      Object.entries(gwSelections || {}).forEach(([position, positionData]) => {
        console.log(`🔎 Processing position=${position} with raw data:`, positionData);
        if (!positionData) return;
        Object.entries(positionData).forEach(([slot, raw]) => {
          console.log(`   🔍 Slot=${slot}, raw=`, raw);
          const playerID = resolvePlayerID(raw);
          if (typeof playerID === "string") {
            selectionCounts[playerID] = (selectionCounts[playerID] || 0) + 1;
            console.log(`✅ Counted selection for playerID=${playerID}`);
          } else {
            console.warn(`⚠️ Could not resolve playerID from`, raw);
          }
        });
      });
    });

    console.log("🧮 Raw selectionCounts before normalization (string keys only):", selectionCounts);

    // Normalize selection counts into percentages, scaled to a 12-manager baseline
    const selectionData = {};
    for (const [maybeID, count] of Object.entries(selectionCounts)) {
      if (typeof maybeID !== "string" || maybeID === "[object Object]") {
        console.warn("   ⚠️ Skipping non-string selection key:", maybeID);
        continue;
      }
      const normalized = totalTeams > 0 ? count / totalTeams : 0;
      selectionData[maybeID] = normalized * (totalTeams / 12);
    }

    console.log("📊 Normalized selectionData:", selectionData);

    return { perfData, selectionData };
  } catch (err) {
    await logError({
      leagueID,
      userID: "system",
      action: "fetchPerfAndSelectionData",
      payload: { gameweek },
      error: err
    });
    throw err;
  }
}

/**
 * Computes price changes for players based on performance and selection data.
 * @param {Array} players - Array of player objects with at least an 'id' and 'currentPrice'.
 * @param {Object} perfData - Performance data keyed by player ID.
 * @param {Object} selectionData - Selection data keyed by player ID (percentage selected).
 * @param {Object} lastSelectionData - Previous selection data keyed by player ID.
 * @returns {Object} priceDeltas keyed by player ID with the computed price change.
 */
export async function computePriceChanges(players, perfData, selectionData, lastSelectionData) {
  try {
    // Extract performance values for median calculation only from defined perfData entries
    const perfValues = Object.values(perfData).filter(v => v !== undefined).sort((a, b) => a - b);
    const medianPerf = perfValues.length
      ? perfValues[Math.floor(perfValues.length / 2)]
      : 0;

    const priceDeltas = {};

    players.forEach(player => {
      const perfRaw = perfData[player.id];
      const didNotPlay = perfRaw === undefined; // no playerPoints doc = DNP
      const perf = didNotPlay ? 0 : perfRaw;
      const sel = selectionData[player.id] || 0;
      const lastSel = lastSelectionData[player.id] || 0;

      // Apply guardrails based on performance score before selection weighting
      let perfChange = 0;
      if (didNotPlay) {
        perfChange = -0.1;
      } else if (perf < 2) {
        perfChange = -0.2;
      } else if (perf === 2) {
        perfChange = -0.1;
      } else if (perf === 3) {
        // cap negative at -0.1, but can be 0 if median is low
        const diff = perf - medianPerf;
        if (diff < 0) {
          perfChange = Math.max(diff * 0.05, -0.1);
        } else {
          perfChange = 0;
        }
      } else if (perf >= 4) {
        // cannot decline (min 0)
        perfChange = Math.max((perf - medianPerf) * 0.05, 0);
      } else {
        // No scores between 3 and 4 (excluding 3 and >=4)
        perfChange = 0;
      }

      // Selection change: difference in selection percentage, scaled
      let selChange = (sel - lastSel) * 0.1; // placeholder scaling
      if (selChange > 0.3) selChange = 0.3;
      if (selChange < -0.3) selChange = -0.3;

      // Combine changes with weights: 80% performance, 20% selection
      let delta = perfChange * 0.8 + selChange * 0.2;

      // Clamp final delta between -0.2 and 0.3
      if (delta > 0.3) delta = 0.3;
      if (delta < -0.2) delta = -0.2;

      delta = Math.round(delta * 10000) / 10000;

      console.log(`computePriceChanges debug | perfChange=${perfChange.toFixed(2)}, sel=${sel.toFixed(2)}, lastSel=${lastSel.toFixed(2)}, selChange=${selChange.toFixed(2)}, combinedDelta=${delta.toFixed(2)}`);

      priceDeltas[player.id] = delta;
    });

    return priceDeltas;
  } catch (err) {
    await logError({
      leagueID: "N/A",
      userID: "system",
      action: "computePriceChanges",
      payload: {},
      error: err
    });
    throw err;
  }
}

/**
 * Applies price changes to Firestore for a given league and gameweek range.
 * @param {string} leagueID - League identifier.
 * @param {number} fromGW - Starting gameweek (inclusive).
 * @param {number} toGW - Ending gameweek (exclusive).
 */
export async function applyPriceChanges(leagueID, fromGW, toGW) {
  const normalizeGWNum = (gw) =>
    typeof gw === "string" ? parseInt(gw.replace(/^GW/, ""), 10) : gw;
  const fromNum = normalizeGWNum(fromGW);
  const toNum = normalizeGWNum(toGW);

  // To track last selection data for selection change calculation
  let lastSelectionData = {};

  for (let gwNum = fromNum; gwNum < toNum; gwNum++) {
    try {
      const gwKey = `GW${gwNum}`;
      console.log(`🔁 applyPriceChanges running for gw=${gwKey}`);
      // Fetch current league players
      const playersCol = paths.leaguePlayers(leagueID);
      const playersSnapshot = await getDocs(playersCol);
      console.log(`📦 playersSnapshot size = ${playersSnapshot.size} for leagueID=${leagueID}, gw=${gwKey}`);
      const players = [];
      playersSnapshot.forEach(docSnap => {
        const data = docSnap.data();
        players.push({
          id: docSnap.id,
          currentPrice: data.currentPrice !== undefined ? data.currentPrice : (data.price || 0),
          priceAccumulator: data.priceAccumulator !== undefined ? data.priceAccumulator : 0,
        });
      });
      // Fetch performance and selection data for this gameweek
      const { perfData, selectionData } = await fetchPerfAndSelectionData(db, leagueID, gwKey);
      // Debug logs
      console.log(`🔎 gw=${gwKey} perfData keys:`, Object.keys(perfData));
      Object.entries(perfData).forEach(([k, v]) => {
        console.log(`   perfData[${k}] = ${v}`);
      });
      console.log(`🔎 gw=${gwKey} selectionData keys:`, Object.keys(selectionData));
      Object.entries(selectionData).forEach(([playerID, value]) => {
        console.log(`   selectionData[${playerID}] = ${value}`);
      });
      // Compute price changes
      const priceDeltas = await computePriceChanges(players, perfData, selectionData, lastSelectionData);
      // Extract medianPerf for logging
      const perfValues = Object.values(perfData).filter(v => v !== undefined).sort((a, b) => a - b);
      const medianPerf = perfValues.length
        ? perfValues[Math.floor(perfValues.length / 2)]
        : 0;
      // Apply updates...
      let updatedCount = 0;
      for (const player of players) {
        const rawDelta = priceDeltas[player.id] || 0;
        let newAccumulator = player.priceAccumulator + rawDelta;
        let newPrice = player.currentPrice;
        while (newAccumulator >= 0.1) {
          newPrice += 0.1;
          newAccumulator -= 0.1;
          newAccumulator = Math.round(newAccumulator * 10000) / 10000;
        }
        while (newAccumulator <= -0.1) {
          newPrice -= 0.1;
          newAccumulator += 0.1;
          newAccumulator = Math.round(newAccumulator * 10000) / 10000;
        }
        // Apply global caps on price
        if (newPrice < 3.5) {
          newPrice = 3.5;
          newAccumulator = 0; // reset accumulator if price hits floor
        }
        if (newPrice > 11.0) {
          newPrice = 11.0;
          newAccumulator = 0; // reset accumulator if price hits ceiling
        }
        newAccumulator = Math.round(newAccumulator * 10000) / 10000;
        const appliedDeltaRaw = newPrice - player.currentPrice;
        const roundedDelta = Math.sign(appliedDeltaRaw) * Math.ceil(Math.abs(appliedDeltaRaw) * 10 - 1e-9) / 10;
        newPrice = Number(newPrice.toFixed(1));
        const playerPerf = perfData[player.id] !== undefined ? perfData[player.id] : "DNP";
        console.log(
          `💰 Updating playerID=${player.id} | oldPrice=${player.currentPrice} | perf=${playerPerf} | medianPerf=${medianPerf} | rawDelta=${rawDelta.toFixed(2)} | accumulator(before)=${player.priceAccumulator.toFixed(4)} | appliedDelta=${roundedDelta} | newPrice=${newPrice} | accumulator(after)=${newAccumulator.toFixed(4)}`
        );
        const playerDocRef = doc(paths.leaguePlayers(leagueID), player.id);
        const nextGWKey = `GW${gwNum + 1}`;
        const updateObj = {
          currentPrice: newPrice,
          priceDelta: roundedDelta,
          priceAccumulator: newAccumulator,
          [`priceHistory.${nextGWKey}`]: newPrice,
        };
        try {
          await updateDoc(playerDocRef, updateObj);
        } catch (err) {
          await logError({
            leagueID,
            userID: "system",
            action: "applyPriceChanges_player_update",
            payload: { playerID: player.id, gwNum },
            error: err
          });
          continue;
        }
        console.log(`   🔄 priceHistory updated: priceHistory.${nextGWKey} = ${newPrice}`);
        updatedCount++;
      }
      console.log(`✅ ${gwKey} price update summary: ${updatedCount} players updated.`);
      lastSelectionData = selectionData;
    } catch (err) {
      await logError({
        leagueID,
        userID: "system",
        action: "applyPriceChanges_loop",
        payload: { fromGW, toGW, currentGW: gwNum },
        error: err
      });
      throw err;
    }
  }
}
